import styled from "@emotion/styled";
import deleteSvg from "assets/svg/delete.svg";
import { NoInformationColumn } from "components/common/NoInformationColumn";
import { HttpStatus } from "constant";
import { SensorChartType, ChartSource } from "constant/chart";
import { AppContext } from "context/appContext";
import { useMetricDefined } from "hooks/useMetricDefined";
import usePanelConfiguration from "hooks/usePanelConfiguration";
import * as _ from "lodash";
import { cloneDeep, get, isEmpty, trim, uniq } from "lodash";
import {
    Fragment,
    FunctionComponent,
    memo,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Collapse from "react-bootstrap/Collapse";
import DropdownItem from "react-bootstrap/DropdownItem";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import { Link, useHistory, useParams } from "react-router-dom";
import dashboardService from "service/dashboardService";
import { showErrorAlert } from "utils/alert";
import { fetchDevices } from "utils/gatewayFunctions";
import { validateChartColors } from "utils/chart";
import create from "zustand";
import {
    CHART_ERROR_MESSAGE,
    CHART_NAME_PATTERN,
    ColorPicker,
    ContainerStyled,
    getMQTTQueryByDevice,
    UnitPicker,
    useCreateDashboardStore,
} from "../create";
import { GatewayChartFor, GateWayChartType } from "../create/GatewayCharts";
import { MultipleChartBlock } from "../create/MultipleChartBlock";
import { OtherChartFor } from "../create/OtherCharts";
import CombinedConsumptionChartUpdate from "./CombinedConsumptionChartUpdate";

const ContentWrapper = styled.div`
    .chart-panel-title {
        font-size: 18px;
    }

    .chart-panel-title i {
        transform: rotate(270deg);
    }

    .chart-panel-title i.active {
        transform: rotate(0deg);
    }

    .chart-panel-title small {
        margin-left: 10px;
        color: #828fab;
        font-size: 13px;
        line-height: 18px;
    }

    .cstm-table .chart-row {
        background-color: var(--create-chart-from-background);
    }

    fieldset[disabled] i,
    fieldset[disabled] button,
    fieldset[disabled] input,
    fieldset[disabled] select,
    fieldset[disabled] img {
        pointer-events: none;
    }
`;

type ChartRowColumn = {
    key: string;
    name: string;
    className?: string;
    render?: (data: any) => ReactNode;
};

type UpdateChartRowProps = {
    [key: string]: {
        type: "row" | "template";
        filter: (data: any) => boolean;
        columns?: ChartRowColumn[];
        template?: (data: any) => ReactNode;
    };
};

const dataStore = create<{
    data: any[];
    setData: (data: any[]) => void;
    updateValue: (uuid: string, key: string, value: any) => void;
    deleteChart: (uuid: string) => void;
    devices: {
        allRegistry: any[];
        deviceList: any[];
    };
    setDevices: (devices: any) => void;
    chartUpdated: string[];
    chartDeleted: string[];
}>((set, get) => ({
    data: [],
    setData: (data) =>
        set({
            data,
            // Clear following values when initialising data
            chartUpdated: [],
            chartDeleted: [],
        }),
    updateValue: (uuid, key, value) => {
        const currentData = get().data.find((item) => item.uuid === uuid);

        if (
            typeof _.get(currentData, key) === "undefined" ||
            _.get(currentData, key) === value
        )
            return;

        set((state) => ({
            data: state.data.map((item) =>
                item.uuid === uuid ? _.set(item, key, value) : item
            ),
            chartUpdated: uniq([...state.chartUpdated, uuid]),
        }));
    },
    deleteChart: (uuid) =>
        set((state) => ({
            data: state.data.filter((item) => item.uuid !== uuid),
            chartDeleted: uniq([...state.chartDeleted, uuid]),
        })),
    devices: {
        allRegistry: [],
        deviceList: [],
    },
    setDevices: (devices) => set({ devices }),
    chartUpdated: [],
    chartDeleted: [],
}));

const saveStore = create<{
    dashboardId: string;
    setDashboardId: (dashboardId: string) => void;
    isSaving: boolean;
    setIsSaving: (isSaving: boolean) => void;
}>((set) => ({
    dashboardId: "",
    setDashboardId: (dashboardId) => set({ dashboardId }),
    isSaving: false,
    setIsSaving: (isSaving) => set({ isSaving }),
}));

export const EditDropdownLink = ({
    panelId,
    ...props
}: { panelId: string } & JSX.IntrinsicAttributes) => {
    const history = useHistory();
    const {
        storeData: {
            dashboard: { currentDashboard },
        },
    } = useContext(AppContext);

    return (
        <DropdownItem
            {...props}
            onClick={() => {
                history.push(
                    `/dashboard/${currentDashboard.key}/update/${panelId}`
                );
            }}
        >
            Edit
        </DropdownItem>
    );
};

export const DeviceMapRequest: Record<
    ChartSource,
    {
        attributes: (attributes: any[]) => any[];
        source: ChartSource | "";
        chart: string;
        name: string;
    }
> = {
    SENSOR: {
        attributes: (attributes: any[]) =>
            attributes.map((item) =>
                objectFluent(
                    {
                        color: "",
                        device_id: (device_id: string) =>
                            device_id.split("/")[0],
                        gateway_id: () => item.gateway_id || item.gatewayId,
                        name: () => item.sensor_type || item.name,
                        said: () => item.said ?? item.SAID,
                        show_alert: false,
                        time_series: getMQTTQueryByDevice(item),
                        unit: "na",
                    },
                    item
                )
            ),
        source: ChartSource.SENSOR,
        chart: "",
        name: "",
    },
    ACTUATOR: {
        attributes: (attributes: any[]) =>
            attributes.map((item) =>
                objectFluent(
                    {
                        color: "",
                        device_id: (device_id: string) =>
                            device_id.split("/")[0],
                        gateway_id: () => item.gateway_id || item.gatewayId,
                        name: () => item.device_name || item.name,
                        said: () => item.said ?? item.SAID,
                        show_alert: false,
                        time_series: getMQTTQueryByDevice(item),
                        unit: "na",
                    },
                    item
                )
            ),
        source: ChartSource.ACTUATOR,
        name: "",
        chart: "TIME",
    },
    GATEWAY: {
        attributes: (attributes: any[]) =>
            attributes.map((item) =>
                objectFluent(
                    {
                        type_: "",
                        gateway_id: "",
                    },
                    item
                )
            ),
        source: "",
        chart: "",
        name: "",
    },
    GATEWAY2: {
        attributes: (attributes: any[]) =>
            attributes.map((item) =>
                objectFluent(
                    {
                        type_: "",
                        gateway_id: "",
                    },
                    item
                )
            ),
        source: "",
        chart: "",
        name: "",
    },
    OTHER: {
        attributes: (attributes: any[]) =>
            attributes.map((item) =>
                objectFluent(
                    {
                        organization_id: "",
                        type_: "",
                    },
                    item
                )
            ),
        chart: "",
        name: "",
        source: "",
    },
    AGGREGATION: {
        attributes: (attributes: any[]) =>
            attributes.map((item) =>
                objectFluent(
                    {
                        color: "",
                        device_id: (device_id: string) =>
                            device_id.split("/")[0],
                        gateway_id: () => item.gateway_id || item.gatewayId,
                        name: () => item.sensor_type || item.name,
                        said: () => item.said ?? item.SAID,
                        time_series: getMQTTQueryByDevice(item),
                        show_alert: false,
                        unit: "na",
                    },
                    item
                )
            ),
        source: ChartSource.AGGREGATION,
        chart: "",
        name: "",
    },
};

const TextWrapComponent = ({ children }: any) => (
    <div className="text-nowrap max-w-180px">{children}</div>
);

const UpdateChartRowObj: UpdateChartRowProps = {
    "Gateway charts": {
        type: "row",
        filter: (item: any) =>
            item.source === ChartSource.GATEWAY &&
            SensorChartType.HEATMAP !== item.chart,
        columns: [
            {
                key: "name",
                name: "Chart Name",
                className: "flex-column flex-md-row gap-2",
                render: (data: any) => {
                    const { updateValue } = dataStore();

                    return (
                        <Form.Control
                            type="text"
                            placeholder="Enter chart name"
                            defaultValue={data.name}
                            onChange={(e) =>
                                updateValue(data.uuid, "name", e.target.value)
                            }
                        />
                    );
                },
            },
            {
                key: "attributes[0].gateway_id",
                name: "Gateway",
                render: (data: any) => {
                    const {
                        devices: { allRegistry },
                    } = dataStore();

                    return allRegistry?.find(
                        (device) =>
                            device.gateway_id ===
                            get(data, "attributes[0].gateway_id")
                    )?.name;
                },
            },
            {
                key: "attributes[0].type_",
                name: "Chart For",
                render: (data: any) =>
                    GatewayChartFor[
                        get(
                            data,
                            "attributes[0].type_",
                            "STATUS"
                        ).capitalize() as keyof typeof GatewayChartFor
                    ],
            },
            {
                key: "chart",
                name: "Chart Type",
                className: "flex-column flex-md-row gap-2",
                render: (data: any) => {
                    const { updateValue } = dataStore();

                    return (
                        <Form.Control
                            as="select"
                            custom
                            defaultValue={String(data.chart).capitalize()}
                            onChange={(e) =>
                                updateValue(
                                    data.uuid,
                                    "chart",
                                    e.target.value.toUpperCase()
                                )
                            }
                        >
                            {Object.keys(GateWayChartType).map((key) => (
                                <option key={key} value={key}>
                                    {
                                        GateWayChartType[
                                            key as keyof typeof GateWayChartType
                                        ]
                                    }
                                </option>
                            ))}
                        </Form.Control>
                    );
                },
            },
        ],
    },
    "Combined sensor charts": {
        type: "row",
        filter: (item: any) =>
            ChartSource.SENSOR === item.source &&
            item.chart === SensorChartType.COMBINED,
        columns: [
            {
                key: "name",
                name: "Chart Name",
                className: "flex-column flex-md-row gap-2",
                render: (data: any) => {
                    const { updateValue } = dataStore();
                    return (
                        <Form.Control
                            type="text"
                            placeholder="Enter chart name"
                            defaultValue={data.name}
                            onChange={(e) =>
                                updateValue(data.uuid, "name", e.target.value)
                            }
                        />
                    );
                },
            },
            {
                key: "sensor",
                name: "Sensor",
                render: (data: any) => {
                    const {
                        devices: { deviceList },
                    } = dataStore();

                    const sensorName = deviceList?.find(
                        (device) =>
                            device.device_id ===
                            get(data, "attributes[0].device_id") +
                                "/" +
                                get(data, "attributes[0].said")
                    )?.device_name;

                    if (!sensorName) {
                        return <NoInformationColumn />;
                    }

                    return sensorName;
                },
            },
            {
                key: "attributes[0].name",
                name: "Type",
                render: (data: any) => {
                    const {
                        devices: { deviceList },
                    } = dataStore();

                    const device = deviceList?.find(
                        (device) =>
                            device.device_id ===
                            get(data, "attributes[0].device_id") +
                                "/" +
                                get(data, "attributes[0].said")
                    );

                    return device?.sensor_type || data.attributes[0].name;
                },
            },
            {
                key: "gateway",
                name: "Gateway",
                render: (data: any) => {
                    const {
                        devices: { allRegistry },
                    } = dataStore();

                    const displayedGateway = allRegistry?.find(
                        (device) =>
                            device.gateway_id ===
                            get(data, "attributes[0].gateway_id")
                    );

                    return displayedGateway?.name;
                },
            },
            {
                key: "attributes[0].device_id",
                name: "LDSU UUID",
                render: (data: any) => (
                    <TextWrapComponent>
                        {get(data, "attributes[0].device_id")}
                    </TextWrapComponent>
                ),
            },
            {
                key: "unit",
                name: "Unit",
                render: (data: any) => {
                    const {
                        updateValue,
                        devices: { deviceList },
                    } = dataStore();
                    const device = deviceList?.find(
                        (device) =>
                            device.device_id ===
                            get(data, "attributes[0].device_id") +
                                "/" +
                                get(data, "attributes[0].said")
                    );

                    return (
                        <div className="d-inline-block">
                            <UnitPicker
                                defaultValue={get(data, "attributes[0].unit")}
                                data={device?.units || device?.unit.split(",")}
                                onChange={(e, unit) =>
                                    updateValue(data.uuid, "unit", unit)
                                }
                            />
                        </div>
                    );
                },
            },
            {
                key: "attributes[0].color",
                name: "Color",
                render: (data: any) => {
                    const { updateValue, data: updateData } = dataStore();
                    const currentData = updateData.find(
                        (item) => item.uuid === data.uuid
                    );
                    const { attributes } = currentData;

                    const updateAttributes = (
                        attributes: any[],
                        color: string
                    ) => {
                        if (attributes.length) {
                            attributes.forEach((attribute: any) => {
                                attribute.color = color;
                            });
                        }
                    };

                    return (
                        <div className="d-inline-block">
                            <ColorPicker
                                defaultValue={data.attributes[0].color}
                                onChange={(event, color) => {
                                    updateAttributes(attributes, color);
                                    updateValue(
                                        data.uuid,
                                        "attributes",
                                        attributes
                                    );
                                }}
                            />
                        </div>
                    );
                },
            },
            {
                key: "attributes[0].show_alert",
                name: "Alert line",
                render: (data: any) => {
                    const {
                        updateValue,
                        devices: { deviceList },
                    } = dataStore();
                    const device = deviceList?.find(
                        (device) =>
                            device.device_id ===
                            get(data, "attributes[0].device_id") +
                                "/" +
                                get(data, "attributes[0].said")
                    );

                    const alertAvailable = get(
                        device,
                        "alert_available",
                        false
                    );

                    return (
                        <div className="d-inline-block">
                            <Form.Check
                                type="switch"
                                id={`switch-alert-${get(
                                    device,
                                    "device_id",
                                    ""
                                )}`}
                                checked={
                                    alertAvailable
                                        ? Boolean(
                                              get(
                                                  data,
                                                  "attributes[0].show_alert"
                                              )
                                          )
                                        : false
                                }
                                disabled={!alertAvailable}
                                onChange={(e) =>
                                    updateValue(
                                        data.uuid,
                                        "attributes[0].show_alert",
                                        e.target.checked
                                    )
                                }
                            />
                        </div>
                    );
                },
            },
        ],
    },
    "Single sensor charts": {
        type: "row",
        filter: (item: any) =>
            item.source === ChartSource.SENSOR &&
            SensorChartType.TIME === item.chart,
        columns: [
            {
                key: "name",
                name: "Chart Name",
                className: "flex-column flex-md-row gap-2",
                render: (data: any) => {
                    const { updateValue } = dataStore();

                    return (
                        <Form.Control
                            type="text"
                            placeholder="Enter chart name"
                            defaultValue={data.name}
                            onChange={(e) =>
                                updateValue(data.uuid, "name", e.target.value)
                            }
                        />
                    );
                },
            },
            {
                key: "sensor",
                name: "Sensor",
                render: (data: any) => {
                    const {
                        devices: { deviceList },
                    } = dataStore();

                    const sensorName = deviceList?.find(
                        (device) =>
                            device.device_id ===
                            get(data, "attributes[0].device_id") +
                                "/" +
                                get(data, "attributes[0].said")
                    )?.device_name;

                    if (!sensorName) {
                        return <NoInformationColumn />;
                    }

                    return sensorName;
                },
            },
            {
                key: "attributes[0].name",
                name: "Type",
                render: (data: any) => {
                    const {
                        devices: { deviceList },
                    } = dataStore();

                    const device = deviceList?.find(
                        (device) =>
                            device.device_id ===
                            get(data, "attributes[0].device_id") +
                                "/" +
                                get(data, "attributes[0].said")
                    );

                    return device?.sensor_type || data.attributes[0].name;
                },
            },
            {
                key: "gateway",
                name: "Gateway",
                render: (data: any) => {
                    const {
                        devices: { allRegistry },
                    } = dataStore();

                    const displayedGateway = allRegistry?.find(
                        (device) =>
                            device.gateway_id ===
                            get(data, "attributes[0].gateway_id")
                    );

                    return displayedGateway?.name;
                },
            },
            {
                key: "attributes[0].device_id",
                name: "LDSU UUID",
                render: (data: any) => (
                    <TextWrapComponent>
                        {get(data, "attributes[0].device_id")}
                    </TextWrapComponent>
                ),
            },
            {
                key: "unit",
                name: "Unit",
                render: (data: any) => {
                    const {
                        updateValue,
                        devices: { deviceList },
                    } = dataStore();
                    const device = deviceList?.find(
                        (device) =>
                            device.device_id ===
                            get(data, "attributes[0].device_id") +
                                "/" +
                                get(data, "attributes[0].said")
                    );

                    return (
                        <div className="d-inline-block">
                            <UnitPicker
                                defaultValue={get(data, "attributes[0].unit")}
                                data={device?.units || device?.unit.split(",")}
                                onChange={(e, unit) =>
                                    updateValue(data.uuid, "unit", unit)
                                }
                            />
                        </div>
                    );
                },
            },
            {
                key: "attributes[0].color",
                name: "Color",
                render: (data: any) => {
                    const { updateValue } = dataStore();
                    return (
                        <div className="d-inline-block">
                            <ColorPicker
                                defaultValue={data.attributes[0].color}
                                onChange={(event, color) => {
                                    updateValue(
                                        data.uuid,
                                        "attributes[0].color",
                                        color
                                    );
                                }}
                            />
                        </div>
                    );
                },
            },
            {
                key: "attributes[0].show_alert",
                name: "Alert line",
                render: (data: any) => {
                    const {
                        updateValue,
                        devices: { deviceList },
                    } = dataStore();
                    const device = deviceList?.find(
                        (device) =>
                            device.device_id ===
                            get(data, "attributes[0].device_id") +
                                "/" +
                                get(data, "attributes[0].said")
                    );

                    const alertAvailable = get(
                        device,
                        "alert_available",
                        false
                    );

                    return (
                        <div className="d-inline-block">
                            <Form.Check
                                type="switch"
                                id={`switch-alert-${get(
                                    device,
                                    "device_id",
                                    ""
                                )}`}
                                checked={
                                    alertAvailable
                                        ? Boolean(
                                              get(
                                                  data,
                                                  "attributes[0].show_alert"
                                              )
                                          )
                                        : false
                                }
                                disabled={!alertAvailable}
                                onChange={(e) =>
                                    updateValue(
                                        data.uuid,
                                        "attributes[0].show_alert",
                                        e.target.checked
                                    )
                                }
                            />
                        </div>
                    );
                },
            },
        ],
    },
    "Multiple sensor charts": {
        type: "template",
        filter: (item: any) =>
            item.source === ChartSource.SENSOR &&
            SensorChartType.STACKED === item.chart,
        template: (data: any) => <MultipleChartUpdateTemplate data={data} />,
    },
    "Actuator charts": {
        type: "row",
        filter: (item: any) => item.source === ChartSource.ACTUATOR,
        columns: [
            {
                key: "name",
                name: "Chart Name",
                className: "flex-column flex-md-row gap-2",
                render: (data: any) => {
                    const { updateValue } = dataStore();

                    return (
                        <Form.Control
                            type="text"
                            placeholder="Enter chart name"
                            defaultValue={data.name}
                            onChange={(e) =>
                                updateValue(data.uuid, "name", e.target.value)
                            }
                        />
                    );
                },
            },
            {
                key: "name",
                name: "Actuator",
                render: (data: any) => {
                    const {
                        devices: { deviceList },
                    } = dataStore();

                    const device = deviceList?.find(
                        (device) =>
                            device.device_id ===
                            get(data, "attributes[0].device_id") +
                                "/" +
                                get(data, "attributes[0].said")
                    );

                    if (!device) {
                        return <NoInformationColumn />;
                    }

                    return device?.device_name;
                },
            },
            {
                key: "type",
                name: "Type",
                render: (data: any) => {
                    const {
                        devices: { deviceList },
                    } = dataStore();

                    const device = deviceList?.find(
                        (device) =>
                            device.device_id ===
                            get(data, "attributes[0].device_id") +
                                "/" +
                                get(data, "attributes[0].said")
                    );

                    return device?.sensor_type || data.attributes[0].name;
                },
            },
            {
                key: "gateway",
                name: "Gateway",
                render: (data: any) => {
                    const {
                        devices: { allRegistry },
                    } = dataStore();

                    return allRegistry?.find(
                        (device) =>
                            device.gateway_id ===
                            get(data, "attributes[0].gateway_id")
                    )?.name;
                },
            },
            {
                key: "attributes[0].device_id",
                name: "LDSU UUID",
            },
            {
                key: "attributes[0].color",
                name: "Color",
                render: (data: any) => {
                    const { updateValue } = dataStore();

                    return (
                        <div className="d-inline-block">
                            <ColorPicker
                                defaultValue={get(data, "attributes[0].color")}
                                onChange={(event, color) => {
                                    updateValue(
                                        data.uuid,
                                        "attributes[0].color",
                                        color
                                    );
                                }}
                            />
                        </div>
                    );
                },
            },
        ],
    },
    "Other charts": {
        type: "row",
        filter: (item: any) => item.source === ChartSource.OTHER,
        columns: [
            {
                key: "name",
                name: "Chart Name",
                className: "flex-column flex-md-row gap-2",
                render: (data: any) => {
                    const { updateValue } = dataStore();

                    return (
                        <Form.Control
                            type="text"
                            placeholder="Enter chart name"
                            defaultValue={data.name}
                            onChange={(e) =>
                                updateValue(data.uuid, "name", e.target.value)
                            }
                        />
                    );
                },
            },
            {
                key: "attributes[0].type_",
                name: "Chart for",
                render: (data: any) =>
                    get(data, "attributes[0].type_") === "COUNT_ALERT"
                        ? OtherChartFor.Notification
                        : OtherChartFor.Event,
            },
        ],
    },
    "Consumption charts": {
        type: "row",
        filter: (item: any) =>
            item.source === ChartSource.AGGREGATION &&
            SensorChartType.COMBINED !== item.chart,
        columns: [
            {
                key: "name",
                name: "Chart Name",
                className: "flex-column flex-md-row gap-2",
                render: (data: any) => {
                    const { updateValue } = dataStore();

                    return (
                        <Form.Control
                            type="text"
                            placeholder="Enter chart name"
                            defaultValue={data.name}
                            onChange={(e) =>
                                updateValue(data.uuid, "name", e.target.value)
                            }
                        />
                    );
                },
            },
            {
                key: "sensor",
                name: "Sensor",
                render: (data: any) => {
                    const {
                        devices: { deviceList },
                    } = dataStore();

                    const sensorName = deviceList?.find(
                        (device) =>
                            device.device_id ===
                            get(data, "attributes[0].device_id") +
                                "/" +
                                get(data, "attributes[0].said")
                    )?.device_name;

                    if (!sensorName) {
                        return <NoInformationColumn />;
                    }

                    return sensorName;
                },
            },
            {
                key: "attributes[0].name",
                name: "Type",
                render: (data: any) => {
                    const {
                        devices: { deviceList },
                    } = dataStore();

                    const device = deviceList?.find(
                        (device) =>
                            device.device_id ===
                            get(data, "attributes[0].device_id") +
                                "/" +
                                get(data, "attributes[0].said")
                    );

                    return device?.sensor_type || data.attributes[0].name;
                },
            },
            {
                key: "gateway",
                name: "Gateway",
                render: (data: any) => {
                    const {
                        devices: { allRegistry },
                    } = dataStore();

                    const displayedGateway = allRegistry?.find(
                        (device) =>
                            device.gateway_id ===
                            get(data, "attributes[0].gateway_id")
                    );

                    return displayedGateway?.name;
                },
            },
            {
                key: "attributes[0].device_id",
                name: "LDSU UUID",
                render: (data: any) => (
                    <TextWrapComponent>
                        {get(data, "attributes[0].device_id")}
                    </TextWrapComponent>
                ),
            },
            {
                key: "attributes[0].color",
                name: "Color",
                render: (data: any) => {
                    const { updateValue } = dataStore();
                    return (
                        <div className="d-inline-block">
                            <ColorPicker
                                defaultValue={data.attributes[0].color}
                                onChange={(event, color) => {
                                    updateValue(
                                        data.uuid,
                                        "attributes[0].color",
                                        color
                                    );
                                }}
                            />
                        </div>
                    );
                },
            },
        ],
    },
    "Combined Consumption charts": {
        type: "template",
        filter: (item: any) =>
            item.source === ChartSource.AGGREGATION &&
            SensorChartType.COMBINED === item.chart,
        template: (data: any) => (
            <CombinedConsumptionChartTemplate data={data} />
        ),
    },
    "Heatmap charts": {
        type: "row",
        filter: (item: any) => SensorChartType.HEATMAP === item.chart,
        columns: [
            {
                key: "name",
                name: "Chart Name",
                className: "flex-column flex-md-row gap-2",
                render: (data: any) => {
                    const { updateValue } = dataStore();

                    return (
                        <Form.Control
                            type="text"
                            placeholder="Enter chart name"
                            defaultValue={data.name}
                            onChange={(e) =>
                                updateValue(data.uuid, "name", e.target.value)
                            }
                        />
                    );
                },
            },
        ],
    },
};

const UpdateChartRow = memo(
    ({ dataChart, columns }: { dataChart: any; columns: any[] }) => {
        const { deleteChart } = dataStore();
        const renderMemo = useMemo(
            () => (fn: Function) => fn(dataChart),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [dataChart.uuid]
        );

        return (
            <div className="table-row chart-row px-3">
                <Row className="align-items-center">
                    {columns.map((column, index) => (
                        <Col
                            lg
                            md={{ span: 12, offset: 0 }}
                            key={index}
                            className={`d-flex d-lg-block flex-row my-2 my-lg-0 ${column.className}`}
                        >
                            <Col
                                lg={{ span: 0 }}
                                xs
                                className="d-block d-lg-none actuator-chart-label-wrapper"
                            >
                                {column.name}:{" "}
                            </Col>

                            <Col className="text-right text-lg-left">
                                {column.render ? (
                                    renderMemo(column.render)
                                ) : (
                                    <>{get(dataChart, `${column.key}`)}</>
                                )}
                            </Col>
                        </Col>
                    ))}
                    <Col
                        lg={{ span: 1 }}
                        className="d-flex justify-content-end"
                    >
                        <img
                            src={deleteSvg}
                            alt="delete"
                            className="cursor-pointer"
                            onClick={() => {
                                deleteChart(dataChart.uuid);
                            }}
                            onKeyDown={() => {
                                deleteChart(dataChart.uuid);
                            }}
                        />
                    </Col>
                </Row>
            </div>
        );
    }
);

const MultipleChartUpdateTemplate: FunctionComponent<{
    data: any;
}> = ({ data }) => {
    const {
        updateValue,
        deleteChart,
        devices: { deviceList },
    } = dataStore();

    const { defaultValue } = usePanelConfiguration({
        panelData: data,
        deviceList,
    });

    return (
        <MultipleChartBlock
            isEdit
            defaultValue={defaultValue}
            blockKey={{ key: data.uuid }}
            onDelete={({ key }) => deleteChart(key)}
            onChange={(updateData) => {
                if (!isEmpty(updateData.data)) {
                    updateValue(data.uuid, "name", updateData.chart_name);

                    const { multipleChartType } = updateData;

                    const updateDataDevices = {
                        ...(_.get(updateData, "data[0].devices") || {}),
                    };

                    if (multipleChartType === "different") {
                        Object.assign(updateDataDevices, {
                            ...(_.get(updateData, "data[1].devices") || {}),
                        });
                    }

                    updateValue(
                        data.uuid,
                        "attributes",
                        Object.values(updateDataDevices)
                    );
                }
            }}
        />
    );
};

const CombinedConsumptionChartTemplate: FunctionComponent<{
    data: any;
}> = ({ data }) => {
    const {
        updateValue,
        deleteChart,
        devices: { deviceList },
    } = dataStore();

    return (
        <CombinedConsumptionChartUpdate
            isMutipleEdit
            devices={deviceList}
            panel={data}
            onChange={(updateData: any) => {
                updateValue(data.uuid, "name", updateData.chartName);
                if (!isEmpty(updateData.data)) {
                    updateValue(
                        data.uuid,
                        "attributes",
                        Object.values(updateData.data)
                    );
                }
            }}
            onDelete={() => deleteChart(data.uuid)}
        />
    );
};

const UpdateChartPanel: FunctionComponent<{ title: string }> = (props) => {
    const [open, setOpen] = useState(true);
    const { columns, filter, type, template } = UpdateChartRowObj[props.title];
    const data = dataStore((state) => state.data);
    const { isSaving } = saveStore();
    const dataCharts = data.filter(filter);

    if (!dataCharts.length) return null;

    return (
        <fieldset disabled={isSaving}>
            <Row className="update-chart-panel">
                <Col sm={12}>
                    <p
                        onClick={() => setOpen(!open)}
                        onKeyDown={() => setOpen(!open)}
                        className="d-flex align-items-center cursor-pointer chart-panel-title"
                    >
                        <i className={`material-icons ${open ? "active" : ""}`}>
                            expand_more
                        </i>
                        {props.title}
                        <small>{dataCharts.length} Chart(s)</small>
                    </p>
                </Col>

                <Collapse in={open}>
                    <div className="w-100 mb-5">
                        <Col sm={12} className="form-box">
                            {type === "row" ? (
                                <Row className="cstm-table">
                                    <Col sm={12}>
                                        <div className="table-head border-0 pt-0">
                                            <Row className="no-checkbox text-white">
                                                {columns?.map(
                                                    (column, index) => (
                                                        <Col key={index}>
                                                            {column.name}
                                                        </Col>
                                                    )
                                                )}
                                                <Col md={{ span: 1 }}></Col>
                                            </Row>
                                        </div>
                                        {dataCharts.map((dataChart: any) => (
                                            <UpdateChartRow
                                                key={dataChart.uuid}
                                                dataChart={dataChart}
                                                columns={columns || []}
                                            />
                                        ))}
                                    </Col>
                                </Row>
                            ) : (
                                dataCharts.map((dataChart: any) => (
                                    <Fragment key={dataChart.uuid}>
                                        {template && template(dataChart)}
                                    </Fragment>
                                ))
                            )}
                        </Col>
                    </div>
                </Collapse>
            </Row>
        </fieldset>
    );
};

const PanelSaveButton: FunctionComponent = () => {
    const { data, chartUpdated, chartDeleted } = dataStore();
    const { dashboardId, isSaving, setIsSaving } = saveStore();
    const history = useHistory();
    useMetricDefined();

    const onSaveHandler = async () => {
        setIsSaving(true);
        const validates = [];
        try {
            for (const chartId of chartUpdated) {
                const saveData = data.find((item) => item.uuid === chartId);
                if (!saveData) continue;

                const source: ChartSource = saveData.source;
                const paramsTemplate = cloneDeep(DeviceMapRequest[source]);
                const params = objectFluent(paramsTemplate, saveData);

                const chartColorValidates = validateChartColors({
                    chart: params.chart,
                    source: params.source,
                    attributes: params.attributes,
                });

                if (chartColorValidates.length) {
                    validates.push(...chartColorValidates);
                }

                if (!CHART_NAME_PATTERN.test(params.name.trim())) {
                    validates.push({
                        message: CHART_ERROR_MESSAGE,
                    });
                }

                if (!validates.length) {
                    await dashboardService.updatePanel(dashboardId, chartId, {
                        ...params,
                        name: trim(params.name.replace(/\s+/g, " ")),
                    });
                }
            }
        } catch (error) {
            console.error("error", error);
        }

        try {
            for (const chartId of chartDeleted) {
                await dashboardService.deletePanel(dashboardId, chartId);
            }
        } catch (error) {
            //
        }

        setIsSaving(false);

        if (validates.length > 0) {
            validates.forEach((validate) => {
                showErrorAlert({
                    message: validate.message,
                });
            });
        } else {
            history.push(`/dashboard`);
        }
    };

    return (
        <Button
            className="ml-2 text-uppercase"
            variant="primary"
            disabled={isSaving}
            onClick={onSaveHandler}
        >
            Save
        </Button>
    );
};

const DashboardUpdateCharts = (props: any) => {
    const history = useHistory();
    const { dashboardId } = useParams() as { dashboardId: string };
    const [loading, setLoading] = useState<boolean>(false);
    const [errorFetch, setErrorFetch] = useState<boolean>(false);
    const { setData, setDevices } = dataStore();
    const { setDashboardId, setIsSaving } = saveStore();

    useEffect(() => {
        fetchDevices().then((res) => {
            setDevices(res);
            useCreateDashboardStore.setState({
                deviceList: res.deviceList || [],
                allRegistry: res.allRegistry || [],
            });
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!dashboardId) history.push("/dashboard");

        setDashboardId(dashboardId);
        setIsSaving(false);
        setLoading(true);
        dashboardService.getPanelDashboard(dashboardId, {}).then((res: any) => {
            if (res.status === HttpStatus.OK) {
                setData(get(res, "data"));
            } else {
                setErrorFetch(true);
            }

            setLoading(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardId]);

    return (
        <ContentWrapper className="page-content">
            {loading ? (
                <Spinner animation="border" variant="primary" />
            ) : (
                <ContainerStyled>
                    <Row>
                        <Col sm="12">
                            <h5 className="page-title">
                                Edit Dashboard Charts
                            </h5>
                        </Col>
                    </Row>

                    {errorFetch ? (
                        <Alert
                            variant="danger"
                            className={`w-100 d-flex justify-content-center ${
                                props.className || ""
                            }`}
                        >
                            Cannot fetch dashboard data from server!&nbsp;
                            <Link className="alert-danger" to="/dashboard">
                                <strong>Back to dashboard!</strong>
                            </Link>
                        </Alert>
                    ) : (
                        <>
                            <Row>
                                <Col sm="12">
                                    {Object.keys(UpdateChartRowObj).map(
                                        (key: string) => (
                                            <UpdateChartPanel
                                                key={key}
                                                title={key}
                                            />
                                        )
                                    )}
                                </Col>
                            </Row>
                            <Row>
                                <Col
                                    sm="12"
                                    className="d-flex justify-content-center mt-4"
                                >
                                    <Button
                                        className="text-uppercase"
                                        variant="secondary"
                                        onClick={() => {
                                            history.push("/dashboard");
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <PanelSaveButton />
                                </Col>
                            </Row>
                        </>
                    )}
                </ContainerStyled>
            )}
        </ContentWrapper>
    );
};

export default DashboardUpdateCharts;
